import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import type { SberAutoLeadPayload } from '@/api/inspection-leads'
import { searchBrands } from '@/data/vehicleDictionary'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import i18n from '@/utils/i18n'
import { STAFF_SBER_INTAKE, type SberLeadIntake } from '@/components/inspections/sberLeadIntake'
import {
  countParsed,
  parseSberAutoMessage,
  type SberAutoParsed,
} from '@/utils/sberautoParse'

interface FormState {
  externalId: string
  clientName: string
  clientPhone: string
  city: string
  brand: string
  model: string
  vin: string
  plate: string
  newCar: string
  inspectionAddress: string
  qualificationNote: string
}

const EMPTY: FormState = {
  externalId: '',
  clientName: '',
  clientPhone: '',
  city: '',
  brand: '',
  model: '',
  vin: '',
  plate: '',
  newCar: '',
  inspectionAddress: '',
  qualificationNote: '',
}

function fromParsed(r: SberAutoParsed): FormState {
  return {
    externalId: r.externalId ?? '',
    clientName: r.clientName ?? '',
    clientPhone: r.clientPhone ?? '',
    city: r.city ?? '',
    brand: r.brand ?? '',
    model: r.model ?? '',
    vin: r.vin ?? '',
    plate: r.plate ?? '',
    newCar: [r.newBrand, r.newModel].filter(Boolean).join(' '),
    inspectionAddress: r.inspectionAddress ?? '',
    qualificationNote: r.qualificationNote ?? '',
  }
}

const nz = (s: string) => (s.trim() === '' ? null : s.trim())

/** Марку из анкеты («ssangyong») приводим к написанию из справочника («SsangYong»). */
async function canonicalBrand(name: string): Promise<string> {
  const q = name.trim()
  if (!q) return q
  const found = await searchBrands(q)
  const exact = found.find((b) => b.name.toLowerCase().replace(/\s/g, '') === q.toLowerCase().replace(/\s/g, ''))
  return exact ? exact.name : q
}

/**
 * «Новый лид СберАвто» — диспетчер вставляет анкету из мессенджера, поля
 * заполняются сами (разбор в браузере), он проверяет и сохраняет. Повторная
 * анкета с тем же номером CRM дополняет существующий лид (адрес осмотра,
 * квалификация), дубль не создаётся. RPC upsert_sberauto_lead.
 */
export function SberAutoLeadDialog({
  onClose,
  onSaved,
  intake = STAFF_SBER_INTAKE,
}: {
  onClose: () => void
  /** Заявка создана или дополнена. */
  onSaved?: (r: { leadId: string; created: boolean }) => void
  intake?: SberLeadIntake
}) {
  const { t } = useTranslation()
  const k = (name: string) => {
    const own = `${intake.textPrefix}.${name}`
    return intake.textPrefix && i18n.exists(own) ? own : `leads.sber.${name}`
  }
  const qc = useQueryClient()
  const [text, setText] = useState('')
  const [form, setForm] = useState<FormState>(EMPTY)
  const [parsedCount, setParsedCount] = useState<number | null>(null)
  const [result, setResult] = useState<{ leadId: string; created: boolean } | null>(null)

  const onPaste = (value: string) => {
    setText(value)
    if (!value.trim()) {
      setParsedCount(null)
      return
    }
    const r = parseSberAutoMessage(value)
    setParsedCount(countParsed(r))
    setForm(fromParsed(r))
    if (r.brand) {
      void canonicalBrand(r.brand).then((b) => setForm((f) => (f.brand === r.brand ? { ...f, brand: b } : f)))
    }
  }

  const debouncedExt = useDebouncedValue(form.externalId.trim(), 300)
  const { data: existing } = useQuery({
    queryKey: ['sberauto-lead-by-ext', intake.id, debouncedExt],
    queryFn: () => intake.findByExternalId(debouncedExt),
    enabled: debouncedExt !== '',
    staleTime: 10_000,
  })
  const willUpdate = !!existing && debouncedExt === form.externalId.trim()

  const mut = useMutation({
    mutationFn: () => {
      const payload: SberAutoLeadPayload = {
        external_id: nz(form.externalId),
        client_name: nz(form.clientName),
        client_phone: nz(form.clientPhone),
        location: nz(form.city),
        vehicle_brand: nz(form.brand),
        vehicle_model: nz(form.model),
        vehicle_vin: nz(form.vin),
        vehicle_license_plate: nz(form.plate),
        trade_in_target_vehicle: nz(form.newCar),
        inspection_address: nz(form.inspectionAddress),
        qualification_note: nz(form.qualificationNote),
        raw_message: text.trim() === '' ? null : text,
      }
      return intake.save(payload)
    },
    onSuccess: (r) => {
      setResult(r)
      intake.invalidate(qc, r.leadId)
      onSaved?.(r)
    },
  })

  const canSave = willUpdate || (form.clientName.trim() !== '' && form.clientPhone.trim() !== '')
  const pending = mut.isPending
  const locked = pending || !!result
  const field = (key: keyof FormState, label: string, extra?: { required?: boolean; upper?: boolean }) => (
    <Input
      label={label}
      required={extra?.required}
      value={form[key]}
      disabled={locked}
      onChange={(e) => {
        const v = extra?.upper ? e.target.value.toUpperCase() : e.target.value
        setForm((f) => ({ ...f, [key]: v }))
      }}
    />
  )

  return (
    <Modal
      title={t(k('title'))}
      onClose={onClose}
      footer={
        result ? (
          <Button variant="primary" onClick={onClose}>
            {t('common.done')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={pending}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={() => mut.mutate()} disabled={pending || !canSave}>
              {pending ? t('leads.sber.saving') : willUpdate ? t(k('update')) : t(k('create'))}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col text-left">
          <span className="stryzh-input-label">{t(k('pasteLabel'))}</span>
          <textarea
            className="stryzh-input-field min-h-[120px] resize-y py-2 leading-snug"
            value={text}
            disabled={locked}
            placeholder={t('leads.sber.pastePlaceholder')}
            onChange={(e) => onPaste(e.target.value)}
          />
          <span className="mt-1 text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
            {parsedCount === null
              ? t(k('pasteHint'))
              : parsedCount > 0
                ? t('leads.sber.parsed', { n: parsedCount })
                : t('leads.sber.parsedNone')}
          </span>
        </label>

        {willUpdate && !result ? (
          <div className="rounded border border-[rgba(247,147,26,0.4)] bg-[rgba(247,147,26,0.1)] p-3 text-sm text-[color:var(--stryzh-color-text-primary)]">
            {t(k('dupWarning'), {
              id: form.externalId.trim(),
              name: existing?.client_name ?? '—',
              status: existing
                ? t(intake.statusLabelKey(existing.status))
                : '',
            })}
          </div>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          {field('externalId', t('leads.sber.fCrm'))}
          {field('city', t('leads.sber.fCity'))}
        </div>
        {field('clientName', t('leads.sber.fName'), { required: !willUpdate })}
        <div className="grid gap-3 sm:grid-cols-2">
          {field('clientPhone', t('leads.sber.fPhone'), { required: !willUpdate })}
          {field('brand', t('leads.sber.fBrand'))}
          {field('model', t('leads.sber.fModel'))}
          {field('vin', t('leads.sber.fVin'), { upper: true })}
          {field('plate', t('leads.sber.fPlate'), { upper: true })}
          {field('newCar', t('leads.sber.fNewCar'))}
        </div>
        {field('inspectionAddress', t('leads.sber.fAddress'))}
        <label className="flex flex-col text-left">
          <span className="stryzh-input-label">{t(k('fNote'))}</span>
          <textarea
            className="stryzh-input-field min-h-[64px] resize-y py-2 leading-snug"
            value={form.qualificationNote}
            disabled={locked}
            onChange={(e) => setForm((f) => ({ ...f, qualificationNote: e.target.value }))}
          />
        </label>

        {result ? (
          <div className="rounded border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-200">
            <div className="font-medium">{result.created ? t(k('created')) : t(k('updated'))}</div>
            <Link to={intake.leadHref(result.leadId)} onClick={onClose} className="mt-2 inline-block underline">
              {t(intake.textPrefix ? k('openCard') : 'leads.manual.openCard')}
            </Link>
          </div>
        ) : null}
        {mut.isError ? (
          <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
            {t('leads.sber.error', { reason: (mut.error as Error).message })}
          </div>
        ) : null}
      </div>
    </Modal>
  )
}
